import { isNegativeAnalysisLabel } from "@/entities/hospital/lib/negative-analysis-labels"

/** booster post_classification.category_group 값. 대분류 이름은 두 체계가 공유할 수 있어 이 키로만 갈라야 한다. */
export const EVALUATION_STAT = "EVALUATION_STAT"
export const SELECTION_EVIDENCE = "SELECTION_EVIDENCE"

export type ReviewCategoryGroup = typeof EVALUATION_STAT | typeof SELECTION_EVIDENCE

/**
 * 선택 근거 파일럿 대상(B2C-478). 서버(reviewed-api `app.review-analysis.*`)에도 같은 목록이 있다.
 *
 * 한쪽만으로는 부족하다 — 프론트만 막으면 API 는 그대로 열려 있고, 서버만 막으면
 * 프론트가 빈 블록을 그린다. 대상이 늘면 양쪽을 함께 바꾼다.
 */
const PILOT_HOSPITAL_ORIGIN_IDS: ReadonlySet<number> = new Set([5381])

export const isSelectionEvidencePilot = (hospitalOriginId: number): boolean =>
  PILOT_HOSPITAL_ORIGIN_IDS.has(hospitalOriginId)

/**
 * 쿼리스트링의 group 을 허용값으로만 좁힌다.
 *
 * 서버는 정의되지 않은 값을 400 으로 끊는다. 사용자가 주소창을 고쳤다고 화면이 오류를
 * 띄울 이유는 없으니, 여기서 평가 통계로 되돌려 기존 링크와 같은 화면을 보여 준다.
 */
export const parseReviewCategoryGroup = (value: string | null): ReviewCategoryGroup =>
  value === SELECTION_EVIDENCE ? SELECTION_EVIDENCE : EVALUATION_STAT

/**
 * 어느 병원에나 있어 변별력이 없는 소분류. 건수는 많지만 "왜 이 병원인가"에 답하지 못한다.
 *
 * 한시 사전이다. 원래 의도는 전역 출현 빈도가 낮은 항목을 위로 올리는 것이었는데,
 * 선택 근거 데이터를 가진 병원이 1곳뿐이라 비교 모집단이 없어 희소도를 계산할 수 없다.
 * B2C-462 배치로 병원이 30곳 이상 쌓이면 자동 희소도로 교체하고 이 사전을 회수한다.
 */
const GENERIC_EVIDENCE_LABELS: ReadonlySet<string> = new Set([
  "설명·상담이 자세함",
  "재방문·지인 추천 의사",
  "마취·통증 관리 만족",
  "가격 합리적·저렴",
  "역세권 접근성",
  "대기 없이 빠른 진료",
])

/**
 * 노출 우선순위. 낮을수록 위다.
 *
 * 0 특색 — 이 병원을 고를 이유가 되는 항목
 * 1 일반 — 어느 병원에나 있는 항목
 * 2 부정 — 건수가 적다는 이유로 "효과 불만 1건"이 1위가 되면 안 된다
 */
const distinctivenessRank = (minorCategory: string): number => {
  const label = minorCategory.trim()
  if (isNegativeAnalysisLabel(label)) return 2
  if (GENERIC_EVIDENCE_LABELS.has(label)) return 1
  return 0
}

/**
 * 특색 우선 정렬. 등급이 같으면 건수순이다.
 *
 * 건수순만 쓰면 "설명·상담이 자세함 102건"이 "일본어 통역·외국인 응대 43건"보다 위에 온다.
 * 사용자가 상위 3건만 본다는 전제에서, 그 자리는 흔한 항목이 아니라 특색이 차지해야 한다.
 */
export const sortByDistinctiveness = <T extends { minorCategory: string; minorCount: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const rankDiff = distinctivenessRank(a.minorCategory) - distinctivenessRank(b.minorCategory)
    return rankDiff !== 0 ? rankDiff : b.minorCount - a.minorCount
  })
